<template>
	<div class="clean-trend-line-chart" ref="refChat"></div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import {
	ToolboxComponent,
	TooltipComponent,
	GridComponent,
	VisualMapComponent,
	LegendComponent,
	BrushComponent,
	DataZoomComponent,
	TitleComponent
} from 'echarts/components';
import { CandlestickChart, LineChart, BarChart } from 'echarts/charts';
import { UniversalTransition } from 'echarts/features';
import { useCommonData } from '@/hooks';
import dayjs from 'dayjs';
import { useChartResize } from '../useChartResize';

const props = defineProps({
	data: {
		type: Array,
		default: () => []
	},
	yAxisSplitLineShow: {
		type: Boolean,
		default: true
	}
});

const { getUpperThemeColor } = useCommonData();

echarts.use([
	ToolboxComponent,
	TooltipComponent,
	GridComponent,
	VisualMapComponent,
	LegendComponent,
	BrushComponent,
	DataZoomComponent,
	CandlestickChart,
	LineChart,
	BarChart,
	CanvasRenderer,
	UniversalTransition,
	TitleComponent
]);

function splitData(rawData) {
	let categoryData = []; // [时间]
	let values = [];
	for (let i = 0; i < rawData.length; i++) {
		const { tradingDay, closePrice = 0 } = rawData[i];
		categoryData.push(dayjs(tradingDay).format('YYYY/MM/DD'));
		values.push(closePrice);
	}
	return {
		categoryData: categoryData,
		values: values
	};
}

let chatInstance = null;
const refChat = ref();

const { startListening, stopListening } = useChartResize(() => chatInstance);

const renderChart = () => {
	// 停止旧的监听
	stopListening();

	if (chatInstance) {
		chatInstance.dispose();
		chatInstance.clear();
		chatInstance = null;
	}

	if (!props.data || !props.data.length) return;
	// 确保数据按时间正序排列
	const rawData = [...props.data];

	const sortedData = rawData.reverse();
	const { categoryData, values } = splitData(sortedData);

	chatInstance = echarts.init(refChat.value);

	const themeColor = getUpperThemeColor.value;

	// 获取坐标轴刻度最大值
	const maxVal = Number(Math.max(...values)) * 1.01;
	// 获取坐标轴刻度最小值
	const minVal = Number(Math.min(...values)) * 0.98;
	// 计算坐标轴分割间隔
	let interval = (maxVal - minVal) / 5;

	// 指定图表的配置项和数据
	const option = {
		grid: {
			top: '0%',
			left: '0%',
			right: '0%',
			height: '100%'
		},
		xAxis: {
			type: 'category',
			data: categoryData,
			axisLine: {
				show: false
			},
			axisLabel: {
				show: false
			}
		},
		yAxis: {
			type: 'value',
			axisLine: {
				show: false
			},
			axisLabel: {
				show: false
			},
			splitNumber: 5,
			interval: interval,
			splitLine: {
				lineStyle: { color: '#F2F3F5', type: 'dashed' },
				show: props.yAxisSplitLineShow
			},
			min: minVal,
			max: maxVal
		},
		series: [
			{
				type: 'line',
				symbol: 'none',
				sampling: 'lttb',
				itemStyle: {
					color: themeColor
				},
				areaStyle: {
					color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
						{
							offset: 0,
							color: themeColor
						},
						{
							offset: 1,
							color: '#FFFFFF'
						}
					])
				},
				data: values
			}
		]
	};

	chatInstance.setOption(option);
	startListening();
};

watch(
	() => props.data,
	() => {
		nextTick(() => {
			renderChart();
		});
	},
	{ immediate: true, deep: true }
);

defineExpose({
	refresh: renderChart
});
</script>

<style lang="less" scoped>
.clean-trend-line-chart {
	width: 100%;
	height: 100%;
}
</style>
